// ---------------------------------------------------------------------------
// PWA_ControlBillet — Offline Validation Queue
// Stores pending ticket validations in IndexedDB when the agent is
// unreachable, and syncs them when connectivity is restored.
// ---------------------------------------------------------------------------

import { agentClient } from '@/services/agent-client'
import { useAppStore } from '@/store/app-store'
import { SYNC_INTERVAL } from '@/lib/constants'
import type { ValidationRequest } from '@/lib/types'

// ---------------------------------------------------------------------------
// IndexedDB record types (internal)
// ---------------------------------------------------------------------------

interface PendingRecord {
  id?: number
  validation: ValidationRequest
  status: 'pending' | 'retrying'
  timestamp: number
  retry_count: number
  last_error: string | null
}

interface SyncedRecord {
  id?: number
  validation: ValidationRequest
  synced_at: number
  validation_id: string
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DB_NAME = 'control_billet_offline'
const DB_VERSION = 1
const STORE_PENDING = 'pending_validations'
const STORE_SYNCED = 'synced_validations'

// ---------------------------------------------------------------------------
// Offline Queue
// ---------------------------------------------------------------------------

class OfflineQueue {
  private db: IDBDatabase | null = null
  private syncInProgress = false
  private syncTimer: ReturnType<typeof setInterval> | null = null
  private onlineHandler: (() => void) | null = null

  // ---- initialization -----------------------------------------------------

  async init(): Promise<void> {
    if (this.db) return

    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        if (!db.objectStoreNames.contains(STORE_PENDING)) {
          const pending = db.createObjectStore(STORE_PENDING, {
            keyPath: 'id',
            autoIncrement: true,
          })
          pending.createIndex('status', 'status', { unique: false })
          pending.createIndex('timestamp', 'timestamp', { unique: false })
        }

        if (!db.objectStoreNames.contains(STORE_SYNCED)) {
          const synced = db.createObjectStore(STORE_SYNCED, {
            keyPath: 'id',
            autoIncrement: true,
          })
          synced.createIndex('synced_at', 'synced_at', { unique: false })
        }
      }

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result
        resolve()
      }

      request.onerror = () => reject(request.error)
    })
  }

  // ---- enqueue ------------------------------------------------------------

  /**
   * Enqueue a validation for later sync.
   */
  async enqueue(validation: ValidationRequest): Promise<void> {
    await this.ensureDb()

    const record: PendingRecord = {
      validation,
      status: 'pending',
      timestamp: Date.now(),
      retry_count: 0,
      last_error: null,
    }

    await this.idbPut(STORE_PENDING, record)
    await this.updatePendingCount()

    // Fire-and-forget sync attempt
    this.syncAll().catch(() => {})
  }

  // ---- sync ---------------------------------------------------------------

  /**
   * Try to send each pending validation via agentClient.
   * Move to synced on success.
   */
  async syncAll(): Promise<void> {
    if (this.syncInProgress || !navigator.onLine) return
    this.syncInProgress = true

    try {
      await this.ensureDb()

      const records = await this.idbGetAll<PendingRecord>(STORE_PENDING)
      const pending = records.filter((r) => r.status === 'pending' || r.status === 'retrying')

      for (const record of pending) {
        await this.syncRecord(record)
      }
    } finally {
      this.syncInProgress = false
      await this.updatePendingCount()
    }
  }

  private async syncRecord(record: PendingRecord): Promise<void> {
    try {
      const result = await agentClient.validateTicket(record.validation)

      if (result.success) {
        // Move to synced store
        const synced: SyncedRecord = {
          validation: record.validation,
          synced_at: Date.now(),
          validation_id: result.validation_id ?? '',
        }

        const tx = this.db!.transaction([STORE_PENDING, STORE_SYNCED], 'readwrite')
        tx.objectStore(STORE_SYNCED).add(synced)
        if (record.id != null) {
          tx.objectStore(STORE_PENDING).delete(record.id)
        }

        await this.idbTxComplete(tx)
      } else {
        // Validation rejected by server -- remove from queue (not retryable)
        if (record.id != null) {
          await this.idbDelete(STORE_PENDING, record.id)
        }
      }
    } catch (err) {
      // Network error: update retry count
      record.retry_count += 1
      record.last_error = err instanceof Error ? err.message : String(err)
      record.status = 'retrying'

      await this.idbPut(STORE_PENDING, record)
    }
  }

  // ---- queries ------------------------------------------------------------

  /**
   * Count pending validations.
   */
  async getPendingCount(): Promise<number> {
    await this.ensureDb()
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_PENDING, 'readonly')
      const request = tx.objectStore(STORE_PENDING).count()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  // ---- background sync control --------------------------------------------

  /**
   * Start periodic background sync and listen for online events.
   */
  startBackgroundSync(): void {
    this.stopBackgroundSync()

    this.syncTimer = setInterval(() => {
      this.syncAll().catch(() => {})
    }, SYNC_INTERVAL)

    this.onlineHandler = () => {
      this.syncAll().catch(() => {})
    }
    window.addEventListener('online', this.onlineHandler)
  }

  /**
   * Stop periodic background sync.
   */
  stopBackgroundSync(): void {
    if (this.syncTimer) {
      clearInterval(this.syncTimer)
      this.syncTimer = null
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler)
      this.onlineHandler = null
    }
  }

  // ---- internal IDB helpers -----------------------------------------------

  private async ensureDb(): Promise<void> {
    if (!this.db) await this.init()
  }

  private async updatePendingCount(): Promise<void> {
    try {
      const count = await this.getPendingCount()
      useAppStore.getState().setPendingCount(count)
    } catch {
      // Ignore count update errors
    }
  }

  private idbPut(storeName: string, record: unknown): Promise<number> {
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readwrite')
      const request = tx.objectStore(storeName).put(record)
      request.onsuccess = () => resolve(request.result as number)
      request.onerror = () => reject(request.error)
    })
  }

  private idbDelete(storeName: string, key: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readwrite')
      const request = tx.objectStore(storeName).delete(key)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  private idbGetAll<T>(storeName: string): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, 'readonly')
      const request = tx.objectStore(storeName).getAll()
      request.onsuccess = () => resolve(request.result as T[])
      request.onerror = () => reject(request.error)
    })
  }

  private idbTxComplete(tx: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const offlineQueue = new OfflineQueue()
